import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Calendar, Users } from "lucide-react";

// Italian month names
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const TYPE_COLORS = {
  matrimonio:      { bg: "#FCE7F3", text: "#9D174D", dot: "#EC4899", emoji: "💍" },
  addio_celibato:  { bg: "#FFEDD5", text: "#9A3412", dot: "#F97316", emoji: "🥃" },
  addio_nubilato:  { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444", emoji: "👯" },
  laurea:          { bg: "#DBEAFE", text: "#1E3A8A", dot: "#3B82F6", emoji: "🎓" },
  compleanno:      { bg: "#FEF3C7", text: "#78350F", dot: "#F59E0B", emoji: "🎂" },
  battesimo:       { bg: "#DCFCE7", text: "#14532D", dot: "#22C55E", emoji: "👶" },
  cresima:         { bg: "#E0E7FF", text: "#312E81", dot: "#6366F1", emoji: "✝️" },
  comunione:       { bg: "#CFFAFE", text: "#155E75", dot: "#06B6D4", emoji: "🕊️" },
  fidanzamento:    { bg: "#FAE8FF", text: "#86198F", dot: "#D946EF", emoji: "💞" },
  cenone_capodanno:    { bg: "#FEF3C7", text: "#7C2D12", dot: "#D97706", emoji: "🎆" },
  pranzo_natale:       { bg: "#FEE2E2", text: "#7F1D1D", dot: "#DC2626", emoji: "🎄" },
  pranzo_pasqua:       { bg: "#FAE8FF", text: "#701A75", dot: "#A855F7", emoji: "🐣" },
  pranzo_pasquetta:    { bg: "#ECFCCB", text: "#3F6212", dot: "#65A30D", emoji: "🧺" },
  pranzo_ferragosto:   { bg: "#E0F2FE", text: "#075985", dot: "#0EA5E9", emoji: "🏖️" },
  cena_ferragosto:     { bg: "#FFE4E6", text: "#9F1239", dot: "#F43F5E", emoji: "🌅" },
  altro:           { bg: "#F1F5F9", text: "#334155", dot: "#64748B", emoji: "✨" },
};

const todayISO = () => new Date().toISOString().split("T")[0];
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function buildMonthGrid(year, month) {
  // Returns 6x7 grid of Date objects starting from Monday
  const first = new Date(year, month, 1);
  // JS getDay: 0=Sun..6=Sat. We want Mon=0..Sun=6
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export default function EventsCalendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [events, setEvents] = useState([]);
  const [dragTarget, setDragTarget] = useState(null);

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.event_date) continue;
      (map[e.event_date] = map[e.event_date] || []).push(e);
    }
    return map;
  }, [events]);

  const prev = () => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 });
  const next = () => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 });
  const goToday = () => setCursor({ year: today.getFullYear(), month: today.getMonth() });

  const onDragStart = (e, ev) => {
    e.dataTransfer.setData("text/plain", ev.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, iso) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragTarget !== iso) setDragTarget(iso);
  };

  const onDrop = async (e, iso) => {
    e.preventDefault();
    setDragTarget(null);
    const eventId = e.dataTransfer.getData("text/plain");
    if (!eventId) return;
    const evObj = events.find((x) => x.id === eventId);
    if (!evObj || evObj.event_date === iso) return;

    // Optimistic update
    setEvents((prevEvs) => prevEvs.map((x) => x.id === eventId ? { ...x, event_date: iso } : x));
    try {
      await api.put(`/events/${eventId}/date`, { event_date: iso });
      toast.success(`Evento spostato al ${iso}`);
    } catch (err) {
      toast.error("Errore aggiornamento data");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Vista temporale</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Calendario Eventi</h1>
          <p className="text-slate-500 mt-2">Trascina un evento per spostarlo di data · click per aprirlo</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="cal-prev" className="btn-ghost border border-slate-200"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={goToday} data-testid="cal-today" className="btn-ghost border border-slate-200">Oggi</button>
          <button onClick={next} data-testid="cal-next" className="btn-ghost border border-slate-200"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-[#0F2741]">{MONTHS[cursor.month]} <span className="text-slate-400">{cursor.year}</span></h2>
        <p className="text-sm text-slate-500">{events.length} eventi totali · {Object.keys(eventsByDate).length} date occupate</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6">
          {cells.map((d, idx) => {
            const iso = toISO(d);
            const isCurrentMonth = d.getMonth() === cursor.month;
            const isToday = iso === todayISO();
            const dayEvents = eventsByDate[iso] || [];
            const isDragOver = dragTarget === iso;

            return (
              <div
                key={idx}
                data-testid={`cal-cell-${iso}`}
                onDragOver={(e) => onDragOver(e, iso)}
                onDragLeave={() => setDragTarget(null)}
                onDrop={(e) => onDrop(e, iso)}
                className={`min-h-[110px] border-r border-b border-slate-100 p-1.5 transition-colors ${!isCurrentMonth ? "bg-slate-50/60" : ""} ${isDragOver ? "bg-[#EFF6FF] ring-2 ring-[#2563EB] ring-inset" : ""}`}
              >
                <div className="flex justify-between items-center mb-1 px-1">
                  <span className={`text-xs font-semibold ${isToday ? "text-white bg-[#2563EB] rounded-full w-6 h-6 grid place-items-center" : !isCurrentMonth ? "text-slate-300" : "text-slate-600"}`}>
                    {d.getDate()}
                  </span>
                  {dayEvents.length > 2 && (
                    <span className="text-[10px] text-slate-400 font-semibold">+{dayEvents.length - 2}</span>
                  )}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((e) => {
                    const c = TYPE_COLORS[e.event_type] || TYPE_COLORS.altro;
                    return (
                      <div
                        key={e.id}
                        draggable
                        onDragStart={(ev) => onDragStart(ev, e)}
                        onClick={() => navigate(`/eventi/${e.id}`)}
                        data-testid={`cal-event-${e.id}`}
                        className="cursor-grab active:cursor-grabbing rounded-md px-1.5 py-1 text-[11px] font-medium truncate hover:shadow-md transition-shadow"
                        style={{ background: c.bg, color: c.text, borderLeft: `3px solid ${c.dot}` }}
                        title={`${e.event_type} · ${e.client_name || "—"} · ${e.guests} ospiti`}
                      >
                        <span className="mr-1">{c.emoji}</span>
                        {e.start_time && <span className="opacity-70 mr-1">{e.start_time}</span>}
                        <span className="truncate">{e.client_name || e.event_type}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Legenda tipologie</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(TYPE_COLORS).map(([k, c]) => (
            <span key={k} className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full" style={{ background: c.bg, color: c.text }}>
              <span className="w-2 h-2 rounded-full" style={{ background: c.dot }} />
              <span>{c.emoji} {k.replace(/_/g, " ")}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
